// Phase 84.M.85 — Rating verification gate.
//
// Pattern song song với `GuestLoginGateContext` (auth gate). Mục đích: chặn
// VĐV chưa xác thực điểm trình KHÔNG cho phép navigate sang trang đăng ký
// giải, và mở luôn `SelfRatingVerificationModal` để upload ảnh OCR ngay.
//
// User choices Phase 84.M.85:
//   • 1a — Áp dụng TẤT CẢ giải (kể cả free/giao hữu).
//   • 2d — Chặn ở cả 2 nơi: pre-navigation từ các nút "Đăng ký" + page-load
//          ở `/athlete/tournaments/.../register`.
//   • 3b — Modal mở luôn `SelfRatingVerificationModal` (upload OCR ngay,
//          không bắt user vòng qua trang Personal).
//   • 4a — Backend cũng chặn (return 403 `rating_unverified`).
//   • 5b — Cho qua nếu user đã submit pending (status pending/needs_review).
//
// Usage:
//   const { requireRatingVerified } = useRatingGate();
//   requireRatingVerified(() => navigate("/.../register"));
//
// Behaviour:
//   1. Nếu user.rating_verified → chạy action ngay.
//   2. Nếu chưa verified, gọi GET /api/athlete/rating-verification/me
//      để check pending. Có pending → chạy action.
//   3. Không có pending → mở SelfRatingVerificationModal. Sau khi user
//      submit thành công → chạy action (auto-resume).
import {
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";
import { api } from "../lib/api";

// v17.11 Lazy Load Đợt 2 — SelfRatingVerificationModal có OCR + camera capture,
// bundle nặng. Chỉ lazy-import khi thực sự mở modal (VĐV chưa verified và
// bấm đăng ký giải).
const SelfRatingVerificationModal = lazy(() =>
  import(
    /* webpackChunkName: "modal-self-rating-verification" */
    "../components/athlete/personal/SelfRatingVerificationModalSmart"
  ),
);

const RatingGateContext = createContext({
  requireRatingVerified: (fn) => fn?.(),
});

export function RatingGateProvider({ children }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const pendingActionRef = useRef(null);
  const [checking, setChecking] = useState(false);

  const requireRatingVerified = useCallback(
    async (action) => {
      // Guest hoặc không có user → để GuestLoginGate xử lý ở tầng ngoài.
      if (!user?.id) {
        return action?.();
      }
      // Đã verified → cho qua ngay.
      if (user.rating_verified) {
        return action?.();
      }
      // Check pending request — nếu có → cho qua (user choice 5b).
      setChecking(true);
      try {
        const r = await api.get("/athlete/rating-verification/me", {
          params: { status: "pending", page_size: 1 },
        });
        const items = Array.isArray(r.data?.items)
          ? r.data.items
          : Array.isArray(r.data)
          ? r.data
          : [];
        const hasPending = items.some(
          (it) => it.status === "pending" || it.status === "needs_review",
        );
        if (hasPending) {
          return action?.();
        }
      } catch {
        // Network lỗi: vẫn chặn để an toàn (BE cũng sẽ chặn lại).
      } finally {
        setChecking(false);
      }
      // Chưa verified + chưa pending → mở modal upload OCR luôn.
      pendingActionRef.current = action || null;
      toast.warning("Bạn cần xác thực điểm trình trước khi đăng ký giải đấu.", {
        description: "Hãy upload ảnh chứng nhận điểm trình để BTC duyệt.",
        duration: 4000,
      });
      setOpen(true);
      return undefined;
    },
    [user?.id, user?.rating_verified],
  );

  const onClose = () => {
    setOpen(false);
    pendingActionRef.current = null;
  };

  const onSubmitted = () => {
    setOpen(false);
    // Sau khi submit OCR thành công, user vào trạng thái pending → cho qua
    // action ban đầu (vd navigate sang trang đăng ký giải).
    const next = pendingActionRef.current;
    pendingActionRef.current = null;
    if (next) {
      // Defer 1 tick để toast + animation modal close kịp render.
      setTimeout(() => {
        try {
          next();
        } catch {
          /* swallow */
        }
      }, 250);
    }
  };

  const value = useMemo(
    () => ({ requireRatingVerified, ratingGateChecking: checking }),
    [requireRatingVerified, checking],
  );

  return (
    <RatingGateContext.Provider value={value}>
      {children}
      {open && (
        <Suspense fallback={null}>
          <SelfRatingVerificationModal
            open={open}
            onClose={onClose}
            onSubmitted={onSubmitted}
          />
        </Suspense>
      )}
    </RatingGateContext.Provider>
  );
}

export function useRatingGate() {
  return useContext(RatingGateContext);
}
